export const DEFAULT_TEMPLATE_CHANNELS = ["fieldglass", "enow"];

export const CASE_CLOSED_TYPES = [
    { key: "corrected", label: "Corrected" },
    { key: "identity_removed", label: "Identity removed" },
];

export const DEFAULT_DEFINITIONS = {
    possible_recipients: ["IDENTITY", "LM_OF_SPONSOR", "SPONSOR", "MSP"],
};

export const DEFAULT_EXPLANATIONS = {
    enable_processing:
        "A flag to enable or disable the processing of this remediation type.",
    step_number: "The order of the steps in the remediation process.",
    recipients: "The parties that will receive the mail templates in each step.",
    maximum_step_duration: "The maximum duration of each step in days.",
    mail_templates:
        "The mail templates to be sent in each step for the different MSPs.",
    mail_templates_case_closed:
        "The mail templates to be sent when the case is closed for the different MSPs.",
};

export function isPlainObject(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function createId(prefix) {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return `${prefix}-${crypto.randomUUID()}`;
    }

    return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function isValidRemediationConfig(data) {
    return (
        isPlainObject(data) &&
        data.type === "REMEDIATION_CONFIG" &&
        isPlainObject(data.definitions) &&
        isPlainObject(data.explanations) &&
        Array.isArray(data.remediation_types)
    );
}

export function getTemplateChannelsFromConfig(data) {
    const channels = new Set(DEFAULT_TEMPLATE_CHANNELS);

    if (!Array.isArray(data?.remediation_types)) {
        return Array.from(channels);
    }

    data.remediation_types.forEach((remediationType) => {
        (remediationType?.steps || []).forEach((step) => {
            Object.keys(step?.channels || {}).forEach((channel) =>
                channels.add(channel)
            );
        });
    });

    return Array.from(channels);
}

function normalizeDefinitions(definitions) {
    const possibleRecipients = Array.isArray(definitions?.possible_recipients)
        ? definitions.possible_recipients.filter(Boolean)
        : DEFAULT_DEFINITIONS.possible_recipients;

    return {
        ...DEFAULT_DEFINITIONS,
        ...(isPlainObject(definitions) ? definitions : {}),
        possible_recipients: possibleRecipients,
    };
}

function normalizeExplanations(explanations) {
    return {
        ...DEFAULT_EXPLANATIONS,
        ...(isPlainObject(explanations) ? explanations : {}),
    };
}

function getTemplateContent(rawValue, fallbackContent = "") {
    if (typeof rawValue === "string") {
        return rawValue;
    }

    if (isPlainObject(rawValue)) {
        if (typeof rawValue.content === "string") {
            return rawValue.content;
        }

        if (typeof rawValue.mail_template === "string") {
            return rawValue.mail_template;
        }
    }

    return fallbackContent;
}

// Builds the per-channel { recipients, mail_template: { content, fileName } } state.
function createChannelState(
    templateChannels = DEFAULT_TEMPLATE_CHANNELS,
    { source = {}, allowedRecipients = null, fallbackContent = "" } = {}
) {
    return templateChannels.reduce((acc, channel) => {
        const channelSource = isPlainObject(source?.[channel]) ? source[channel] : {};

        const rawRecipients = Array.isArray(channelSource.recipients)
            ? channelSource.recipients
            : [];

        const recipients = Array.isArray(allowedRecipients)
            ? rawRecipients.filter((recipient) =>
                allowedRecipients.includes(recipient)
            )
            : rawRecipients.filter(Boolean);

        acc[channel] = {
            recipients: [...recipients],
            mail_template: {
                content: getTemplateContent(
                    channelSource.mail_template,
                    fallbackContent
                ),
                fileName: "",
            },
        };

        return acc;
    }, {});
}

/*
 * API contract:
 *
 * mail_templates_case_closed: {
 *     corrected: "<html>...</html>",
 *     identity_removed: "<html>...</html>"
 * }
 *
 * The API values must be strings. The { content, fileName } wrapper exists
 * only inside the frontend state so uploads and text editing can be handled.
 */
function createCaseClosedState(closureTypes = CASE_CLOSED_TYPES, source = {}) {
    return closureTypes.reduce((acc, { key }) => {
        const templateContent = source?.[key];

        acc[key] = {
            content:
                typeof templateContent === "string" ? templateContent : "",
            fileName: "",
        };

        return acc;
    }, {});
}

export function createStep({
    templateChannels = DEFAULT_TEMPLATE_CHANNELS,
    maximumStepDuration = "7",
    channelsSource = {},
    allowedRecipients = null,
} = {}) {
    return {
        id: createId("step"),
        maximum_step_duration:
            maximumStepDuration === null || maximumStepDuration === undefined
                ? ""
                : String(maximumStepDuration),
        channels: createChannelState(templateChannels, {
            source: channelsSource,
            allowedRecipients,
        }),
    };
}

export function createRemediationType({
    templateChannels = DEFAULT_TEMPLATE_CHANNELS,
    name = "NEW_REMEDIATION_TYPE",
    enableProcessing = true,
    steps = null,
    caseClosedSource = {},
} = {}) {
    return {
        id: createId("type"),
        name,
        enable_processing: Boolean(enableProcessing),
        steps: Array.isArray(steps)
            ? steps
            : [createStep({ templateChannels })],
        mail_templates_case_closed: createCaseClosedState(
            CASE_CLOSED_TYPES,
            caseClosedSource
        ),
    };
}

export function createInitialRemediationTypes(
    templateChannels = DEFAULT_TEMPLATE_CHANNELS
) {
    return [
        createRemediationType({
            templateChannels,
            name: "SPONSOR_INVALID",
            enableProcessing: true,
            steps: [
                createStep({
                    templateChannels,
                    maximumStepDuration: "7",
                    channelsSource: {
                        fieldglass: {
                            recipients: ["IDENTITY", "MSP"],
                            mail_template: "<html></html>",
                        },
                        enow: {
                            recipients: ["IDENTITY"],
                            mail_template: "<html></html>",
                        },
                    },
                }),
                createStep({
                    templateChannels,
                    maximumStepDuration: "7",
                    channelsSource: {
                        fieldglass: {
                            recipients: ["LM_OF_SPONSOR", "SPONSOR"],
                            mail_template: "<html></html>",
                        },
                        enow: {
                            recipients: ["SPONSOR"],
                            mail_template: "<html></html>",
                        },
                    },
                }),
            ],
        }),
    ];
}

function normalizeStepFromConfig(step, definitions, templateChannels) {
    return createStep({
        templateChannels,
        maximumStepDuration: step?.maximum_step_duration ?? "",
        channelsSource: isPlainObject(step?.channels) ? step.channels : {},
        allowedRecipients: definitions.possible_recipients || [],
    });
}

function normalizeRemediationTypeFromConfig(
    remediationType,
    definitions,
    templateChannels
) {
    const normalizedSteps = Array.isArray(remediationType?.steps)
        ? remediationType.steps.map((step) =>
            normalizeStepFromConfig(step, definitions, templateChannels)
        )
        : [];

    return createRemediationType({
        templateChannels,
        name: remediationType?.name || "NEW_REMEDIATION_TYPE",
        enableProcessing: remediationType?.enable_processing ?? true,
        steps: normalizedSteps,
        caseClosedSource: remediationType?.mail_templates_case_closed || {},
    });
}

export function normalizeConfigForState(data) {
    const definitions = normalizeDefinitions(data?.definitions);
    const explanations = normalizeExplanations(data?.explanations);
    const templateChannels = getTemplateChannelsFromConfig(data);

    const remediationTypes = Array.isArray(data?.remediation_types)
        ? data.remediation_types.map((remediationType) =>
            normalizeRemediationTypeFromConfig(
                remediationType,
                definitions,
                templateChannels
            )
        )
        : createInitialRemediationTypes(templateChannels);

    return {
        definitions,
        explanations,
        templateChannels,
        remediationTypes,
    };
}

function channelsToPayload(templateChannels, channels, possibleRecipients) {
    return templateChannels.reduce((acc, channel) => {
        const channelState = channels?.[channel] || {};

        const recipients = Array.isArray(channelState.recipients)
            ? channelState.recipients
            : [];

        acc[channel] = {
            recipients: recipients.filter((recipient) =>
                possibleRecipients.includes(recipient)
            ),
            mail_template: channelState.mail_template?.content || "",
        };

        return acc;
    }, {});
}

/*
 * Converts the frontend-only state back to the API string structure.
 *
 * The payload can therefore never contain:
 *
 * corrected: { content: "...", fileName: "..." }
 *
 * or:
 *
 * corrected: { mail_template: "...", template_name: "..." }
 */
function caseClosedToPayload(closureTypes, caseClosed) {
    return closureTypes.reduce((acc, { key }) => {
        const templateContent = caseClosed?.[key]?.content;

        acc[key] =
            typeof templateContent === "string" ? templateContent : "";

        return acc;
    }, {});
}

function normalizeDurationForPayload(value) {
    if (value === "" || value === null || value === undefined) {
        return null;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : null;
}

export function buildSavePayload({
    definitions,
    explanations,
    templateChannels,
    remediationTypes,
}) {
    return {
        type: "REMEDIATION_CONFIG",
        definitions,
        explanations,
        remediation_types: remediationTypes.map((remediationType) => ({
            name: remediationType.name,
            enable_processing: Boolean(remediationType.enable_processing),
            steps: remediationType.steps.map((step, stepIndex) => ({
                step_number: stepIndex,
                maximum_step_duration: normalizeDurationForPayload(
                    step.maximum_step_duration
                ),
                channels: channelsToPayload(
                    templateChannels,
                    step.channels,
                    definitions.possible_recipients
                ),
            })),
            mail_templates_case_closed: caseClosedToPayload(
                CASE_CLOSED_TYPES,
                remediationType.mail_templates_case_closed
            ),
        })),
    };
}

export function getDurationSummary(duration) {
    if (duration === "" || duration === null || duration === undefined) {
        return "No duration";
    }

    return `${duration} day${String(duration) === "1" ? "" : "s"}`;
}

// Count of unique recipients across all channels of a step.
export function getStepRecipientCount(step) {
    const unique = new Set();

    Object.values(step?.channels || {}).forEach((channelState) => {
        (channelState?.recipients || []).forEach((recipient) =>
            unique.add(recipient)
        );
    });

    return unique.size;
}